import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { useInView } from 'react-intersection-observer';

const TestimonialsSection = styled.section`
  padding: 120px 0;
  background: ${props => props.theme.colors.darkGray};
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 80px;
`;

const Title = styled(motion.h2)`
  font-family: ${props => props.theme.fonts.display};
  font-size: clamp(2rem, 4vw, 3.5rem);
  font-weight: 700;
  margin-bottom: 24px;
  color: ${props => props.theme.colors.white};
`;

const CarouselContainer = styled.div`
  position: relative;
  overflow: hidden;
  height: auto;
  min-height: 450px;

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    min-height: 400px;
  }
`;

const CarouselTrack = styled.div`
  display: flex;
  gap: 40px;
  height: 100%;
  animation: scroll 30s linear infinite;

  &:hover {
    animation-play-state: paused;
  }

  @keyframes scroll {
    0% {
      transform: translateX(0);
    }
    100% {
      transform: translateX(calc(-460px * 6));
    }
  }

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    @keyframes scroll {
      0% {
        transform: translateX(0);
      }
      100% {
        transform: translateX(calc(-320px * 6));
      }
    }
  }
`;

const TestimonialCard = styled(motion.div)`
  background: linear-gradient(135deg, ${props => props.theme.colors.mediumGray}60, ${props => props.theme.colors.dark}40);
  border: 1px solid ${props => props.theme.colors.primary}30;
  border-radius: 20px;
  padding: 40px;
  position: relative;
  overflow: hidden;
  min-width: 420px;
  height: auto;
  min-height: 380px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;

  &:before {
    content: '"';
    position: absolute;
    top: 20px;
    left: 30px;
    font-size: 4rem;
    color: ${props => props.theme.colors.primary}30;
    font-family: serif;
    line-height: 1;
  }

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    min-width: 350px;
    min-height: 350px;
  }

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    min-width: 280px;
    min-height: 320px;
    padding: 24px;
    border-radius: 16px;

    &:before {
      font-size: 2.5rem;
      top: 12px;
      left: 16px;
    }
  }
`;

const Indicators = styled.div`
  display: flex;
  justify-content: center;
  gap: 12px;
  margin-top: 40px;
`;

const Indicator = styled.button`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  border: none;
  background: ${props => props.active ? props.theme.colors.primary : props.theme.colors.mediumGray};
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: ${props => props.theme.colors.primary};
  }
`;

const TestimonialText = styled.p`
  color: ${props => props.theme.colors.lightGray};
  font-size: 1rem;
  line-height: 1.6;
  margin-bottom: 16px;
  position: relative;
  z-index: 2;
  flex: 1;
  
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    font-size: 0.95rem;
    line-height: 1.5;
    margin-bottom: 12px;
  }
`;

const Stars = styled.div`
  display: flex;
  gap: 4px;
  margin-bottom: 16px;
  font-size: 1.2rem;
  color: #FFD700;

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    font-size: 1rem;
    margin-bottom: 12px;
  }
`;

const TestimonialAuthor = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const AuthorAvatar = styled.div`
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: linear-gradient(135deg, ${props => props.theme.colors.primary}, ${props => props.theme.colors.secondary});
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  color: ${props => props.theme.colors.dark};
  font-weight: bold;
`;

const AuthorInfo = styled.div``;

const AuthorName = styled.div`
  color: ${props => props.theme.colors.white};
  font-weight: 600;
  font-size: 1.1rem;
  margin-bottom: 4px;
`;

const AuthorTitle = styled.div`
  color: ${props => props.theme.colors.lightGray};
  font-size: 0.9rem;
`;

const ResultHighlight = styled.div`
  background: ${props => props.theme.colors.success}10;
  border: 1px solid ${props => props.theme.colors.success}30;
  border-radius: 8px;
  padding: 12px 16px;
  margin-top: 20px;
  color: ${props => props.theme.colors.success};
  font-weight: 600;
  font-size: 0.9rem;
  text-align: center;
`;

const Testimonials = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });



  const testimonials = [
    {
      text: "Fui muito bem atendido pelo Eduardo e posteriormente pelo Léo! Deixei 2 veículos consignados, com venda efetiva, extremamente rápida e segura. Todo o acordado foi realizado perfeitamente, sem nenhum problema ou atraso!",
      author: "Leandro Silva",
      title: "Cliente LM Motors",
      avatar: "LS",
      result: "2 veículos vendidos rapidamente"
    },
    {
      text: "Em duas semanas já tive duas ofertas no carro e o vendedor Eduardo conduziu tudo de forma excelente, resolvendo os pequenos problemas no processo e facilitando a conclusão do negócio. Pagamento em dia conforme o valor acertado.",
      author: "Francys Krieck",
      title: "Cliente LM Motors",
      avatar: "FK",
      result: "Vendido em 2 semanas"
    },
    {
      text: "Fiz consignação virtual de um Mini Cooper JCW, carro bem específico e de mercado mais restrito, e para minha grata surpresa, a LM vendeu ele em menos de 24h. Fui atendido muito bem pelo Eduardo Ouriques, super atencioso e profissional.",
      author: "Bruno Costa",
      title: "Cliente LM Motors",
      avatar: "BC",
      result: "Mini Cooper vendido em 24h"
    },
    {
      text: "Fui muito bem atendido pelo Eduardo, sempre muito claro e transparente com tudo! Senti segurança do início ao fim da negociação! Já deixei 2 carros lá pra venda e foi só alegria!",
      author: "Mauriti Junior",
      title: "Cliente LM Motors",
      avatar: "MJ",
      result: "2 carros consignados com sucesso"
    },
    {
      text: "Deixei um carro em consignação e o mesmo foi vendido em menos de 10 dias. Principalmente pelo vendedor Eduardo tivemos êxito na negociação, se trata de uma pessoa íntegra e responsável pelo atendimento ímpar ao cliente!",
      author: "Fabio Gauza",
      title: "Cliente LM Motors",
      avatar: "FG",
      result: "Vendido em menos de 10 dias"
    },
    {
      text: "Já venderam dois carros meus que deixei consignado, não levou duas semanas, o pagamento é realizado certinho, sem enrolação. O proprietário Leonardo é uma pessoa muito íntegra e honesta, e a sua equipe é fantástica.",
      author: "CB Centro Automotivo",
      title: "Cliente LM Motors",
      avatar: "CB",
      result: "2 carros vendidos em 2 semanas"
    }
  ];

  return (
    <TestimonialsSection ref={ref}>
      <Container>
        <Header>
          <Title
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
          >
CLIENTES REAIS DA LM MOTORS
          </Title>
        </Header>

        <CarouselContainer>
          <CarouselTrack>
            {[...testimonials, ...testimonials].map((testimonial, index) => (
              <TestimonialCard
                key={index}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={inView ? { opacity: 1, scale: 1 } : {}}
                transition={{ duration: 0.6, delay: (index % testimonials.length) * 0.1 }}
              >
                <div>
                  <Stars>
                    <span>⭐</span>
                    <span>⭐</span>
                    <span>⭐</span>
                    <span>⭐</span>
                    <span>⭐</span>
                  </Stars>
                  <TestimonialText>{testimonial.text}</TestimonialText>
                  <TestimonialAuthor>
                    <AuthorAvatar>{testimonial.avatar}</AuthorAvatar>
                    <AuthorInfo>
                      <AuthorName>{testimonial.author}</AuthorName>
                      <AuthorTitle>{testimonial.title}</AuthorTitle>
                    </AuthorInfo>
                  </TestimonialAuthor>
                </div>
                <ResultHighlight>{testimonial.result}</ResultHighlight>
              </TestimonialCard>
            ))}
          </CarouselTrack>
        </CarouselContainer>
      </Container>
    </TestimonialsSection>
  );
};

export default Testimonials;